// Indicadores do Dashboard Operacional (ADR-0032, issue #89): volume, taxas
// de divergência, relavagem interna e reclamação, por período ou agrupados por
// Cliente/Categoria/Tipo de Peça. Função pura — sem DOM, sem I/O.

import { somarDias, semanaISO, type DataISO } from "./datas.ts";
import type { Id } from "./ids.ts";
import { MOTIVO_RELAVAGEM } from "./listas.ts";
import type { Snapshot } from "./snapshot.ts";

export type Granularidade = "dia" | "semana" | "mes" | "ano";

/** Como os números são agrupados. `total` = um grupo só, chave "". */
export type Agrupamento =
  | { por: "total" }
  | { por: "periodo"; granularidade: Granularidade }
  | { por: "cliente" }
  | { por: "categoria" }
  | { por: "tipo" };

export interface FiltroIndicadores {
  dataInicio: DataISO;
  dataFim: DataISO;
  clienteIds?: readonly Id[];
  categoriaIds?: readonly Id[];
  tipoIds?: readonly Id[];
}

export interface Indicadores {
  /** Σ Recebida das OS que não são de Relavagem, pela coleta. */
  recebido: number;
  /** O total entregue (Devolvida própria + carona devolvida) das OS fechadas, pela devolução. */
  devolvidas: number;
  /** O que foi lavado, pela coleta da OS em que foi lavado. */
  lavado: {
    /** Conferida (Recebida enquanto a OS está aberta) das OS normais. */
    comanda: number;
    /** Recebida das OS de Relavagem. */
    reclamacao: number;
    /** Peças que saíram do Backlog nesta OS: carona devolvida + retida de novo. */
    backlog: number;
  };
  /** |Recebida − Conferida| das OS fechadas, pela coleta (ADR-0032 §1). */
  divergencia: {
    pecas: number;
    /** Σ Recebida das mesmas OS. */
    base: number;
    porMotivo: Record<string, number>;
  };
  /** Retidas com o motivo "Relavagem", pela devolução (ADR-0032 §2). */
  relavagem: {
    interna: number;
    /** Σ Conferida das OS normais devolvidas no período — base da relavagem e da reclamação. */
    base: number;
  };
  /** Recebida das OS de Relavagem, pela coleta delas. */
  reclamacao: number;
  /** OS normais coletadas no período e ainda não fechadas (fora das taxas). */
  osAbertas: number;
}

export function indicadoresVazios(): Indicadores {
  return {
    recebido: 0,
    devolvidas: 0,
    lavado: { comanda: 0, reclamacao: 0, backlog: 0 },
    divergencia: { pecas: 0, base: 0, porMotivo: {} },
    relavagem: { interna: 0, base: 0 },
    reclamacao: 0,
    osAbertas: 0,
  };
}

const taxa = (parte: number, todo: number): number | null => (todo > 0 ? parte / todo : null);
export const taxaDivergencia = (i: Indicadores): number | null => taxa(i.divergencia.pecas, i.divergencia.base);
export const taxaRelavagem = (i: Indicadores): number | null => taxa(i.relavagem.interna, i.relavagem.base);
export const taxaReclamacao = (i: Indicadores): number | null => taxa(i.reclamacao, i.relavagem.base);
export const totalLavado = (i: Indicadores): number => i.lavado.comanda + i.lavado.reclamacao + i.lavado.backlog;

export function chaveDoPeriodo(dia: DataISO, granularidade: Granularidade): string {
  switch (granularidade) {
    case "dia":
      return dia;
    case "semana":
      return semanaISO(dia);
    case "mes":
      return dia.slice(0, 7);
    case "ano":
      return dia.slice(0, 4);
  }
}

/** Uma chave por período do intervalo, em ordem — nunca pula um período vazio. */
export function periodosDoIntervalo(dataInicio: DataISO, dataFim: DataISO, granularidade: Granularidade): string[] {
  const periodos: string[] = [];
  for (let dia = dataInicio; dia <= dataFim; dia = somarDias(dia, 1)) {
    const chave = chaveDoPeriodo(dia, granularidade);
    if (periodos[periodos.length - 1] !== chave) periodos.push(chave);
  }
  return periodos;
}

/** O intervalo de mesmo tamanho logo antes (para "vs. período anterior"). */
export function periodoAnterior(dataInicio: DataISO, dataFim: DataISO): { dataInicio: DataISO; dataFim: DataISO } {
  let dias = 0;
  for (let d = dataInicio; d <= dataFim; d = somarDias(d, 1)) dias++;
  return { dataInicio: somarDias(dataInicio, -dias), dataFim: somarDias(dataInicio, -1) };
}

/**
 * Soma os indicadores do ADR-0032 no recorte, um grupo por chave do
 * agrupamento. Cada número usa a sua data: divergência, volumes e reclamação
 * pela coleta; relavagem interna, a base dela e devolvidas pela devolução. Só
 * OS fechada entra nas taxas; a OS de Relavagem fica fora do Recebido e das
 * bases; Lote cancelado fica fora de tudo. Agrupado por período, devolve todos
 * os períodos do intervalo, mesmo vazios, em ordem.
 */
export function indicadoresDashboard(
  snapshot: Pick<Snapshot, "lotes" | "contagens" | "retencoes" | "baixasBacklog" | "baixasBacklogInicial" | "tiposPeca">,
  filtro: FiltroIndicadores,
  agrupamento: Agrupamento,
): Map<string, Indicadores> {
  const lotePorId = new Map(snapshot.lotes.filter((l) => !l.canceladoEm).map((l) => [l.id, l]));
  const categoriaPorTipo = new Map(snapshot.tiposPeca.map((t) => [t.id, t.categoriaId]));
  const clientes = filtro.clienteIds ? new Set(filtro.clienteIds) : null;
  const categorias = filtro.categoriaIds ? new Set(filtro.categoriaIds) : null;
  const tipos = filtro.tipoIds ? new Set(filtro.tipoIds) : null;

  const grupos = new Map<string, Indicadores>();
  if (agrupamento.por === "total") grupos.set("", indicadoresVazios());
  if (agrupamento.por === "periodo") {
    for (const p of periodosDoIntervalo(filtro.dataInicio, filtro.dataFim, agrupamento.granularidade)) {
      grupos.set(p, indicadoresVazios());
    }
  }
  /** OS abertas já contadas por grupo — uma OS conta uma vez por grupo. */
  const abertasContadas = new Map<string, Set<Id>>();

  const chave = (clienteId: Id, tipoId: Id, data: DataISO): string => {
    switch (agrupamento.por) {
      case "total":
        return "";
      case "periodo":
        return chaveDoPeriodo(data, agrupamento.granularidade);
      case "cliente":
        return clienteId;
      case "categoria":
        return categoriaPorTipo.get(tipoId) ?? "";
      case "tipo":
        return tipoId;
    }
  };
  /** O grupo da linha nesta data, ou `null` se a data ou o recorte a deixam de fora. */
  const grupo = (clienteId: Id, tipoId: Id, data: DataISO | null): Indicadores | null => {
    if (!data || data < filtro.dataInicio || data > filtro.dataFim) return null;
    if (clientes && !clientes.has(clienteId)) return null;
    if (tipos && !tipos.has(tipoId)) return null;
    if (categorias) {
      const cat = categoriaPorTipo.get(tipoId);
      if (cat == null || !categorias.has(cat)) return null;
    }
    const k = chave(clienteId, tipoId, data);
    let g = grupos.get(k);
    if (!g) grupos.set(k, (g = indicadoresVazios()));
    return g;
  };

  for (const c of snapshot.contagens) {
    const lote = lotePorId.get(c.loteId);
    if (!lote) continue;
    const fechada = lote.devolvidoEm != null;
    const naColeta = grupo(lote.clienteId, c.tipoId, lote.coletaData);
    if (naColeta) {
      if (lote.relavagem) {
        naColeta.reclamacao += c.recebida;
        naColeta.lavado.reclamacao += c.recebida;
      } else {
        naColeta.recebido += c.recebida;
        if (fechada) {
          const conferida = c.conferida ?? c.recebida;
          naColeta.lavado.comanda += conferida;
          naColeta.divergencia.base += c.recebida;
          const pecas = Math.abs(c.recebida - conferida);
          if (pecas > 0) {
            naColeta.divergencia.pecas += pecas;
            const motivo = c.conferidaMotivo ?? "";
            naColeta.divergencia.porMotivo[motivo] = (naColeta.divergencia.porMotivo[motivo] ?? 0) + pecas;
          }
        } else {
          naColeta.lavado.comanda += c.recebida;
          const k = chave(lote.clienteId, c.tipoId, lote.coletaData);
          let vistas = abertasContadas.get(k);
          if (!vistas) abertasContadas.set(k, (vistas = new Set()));
          if (!vistas.has(lote.id)) {
            vistas.add(lote.id);
            naColeta.osAbertas++;
          }
        }
      }
    }
    const naDevolucao = grupo(lote.clienteId, c.tipoId, lote.devolvidoEm);
    if (naDevolucao) {
      naDevolucao.devolvidas += c.devolvida;
      if (!lote.relavagem) naDevolucao.relavagem.base += c.conferida ?? c.recebida;
    }
  }

  for (const r of snapshot.retencoes) {
    if (r.motivo !== MOTIVO_RELAVAGEM) continue;
    const lote = lotePorId.get(r.loteId);
    if (!lote) continue;
    const g = grupo(lote.clienteId, r.tipoId, lote.devolvidoEm);
    if (g) g.relavagem.interna += r.quantidade;
  }

  // Backlog reprocessado: o que saiu do Backlog na OS (as duas tabelas de baixa,
  // os dois destinos), na coleta dessa OS.
  // A carona devolvida também é entrega: soma nas Devolvidas, pela devolução
  // (`ContagemLote.devolvida` guarda só a parte própria da OS).
  for (const b of [...snapshot.baixasBacklog, ...snapshot.baixasBacklogInicial]) {
    const lote = lotePorId.get(b.loteId);
    if (!lote) continue;
    const g = grupo(lote.clienteId, b.tipoId, lote.coletaData);
    if (g) g.lavado.backlog += b.quantidade;
    if (b.destino === "devolvida") {
      const d = grupo(lote.clienteId, b.tipoId, lote.devolvidoEm);
      if (d) d.devolvidas += b.quantidade;
    }
  }

  return grupos;
}
